import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { MemberLesson } from './member-access.contract'
import {
  changeMemberPassword,
  completeMemberLesson,
  consumeMemberMagic,
  fetchMemberBrand,
  fetchMemberCatalogOffer,
  fetchMemberLesson,
  fetchMemberLibrary,
  fetchMemberMe,
  fetchMemberProduct,
  loginMemberPassword,
  patchMemberMe,
  recordMemberProgress,
  requestMemberLogin,
  uploadMemberAvatar,
  verifyMemberCode,
} from './member-access.api'

export const memberAccessKeys = {
  library: ['member-access', 'library'] as const,
  me: ['member-access', 'me'] as const,
  brand: (producerId: string) => ['member-access', 'brand', producerId] as const,
  product: (productId: string) => ['member-access', 'product', productId] as const,
  catalog: (productId: string) => ['member-access', 'catalog', productId] as const,
  lesson: (productId: string, lessonId: string) => ['member-access', 'lesson', productId, lessonId] as const,
}

export function useMemberLibraryQuery(enabled: boolean) {
  return useQuery({
    queryKey: memberAccessKeys.library,
    queryFn: fetchMemberLibrary,
    enabled,
    refetchOnWindowFocus: true,
    staleTime: 0,
  })
}

export function useMemberBrandQuery(producerId: string) {
  return useQuery({
    queryKey: memberAccessKeys.brand(producerId),
    queryFn: () => fetchMemberBrand(producerId),
  })
}

export function useMemberMeQuery(enabled: boolean) {
  return useQuery({
    queryKey: memberAccessKeys.me,
    queryFn: fetchMemberMe,
    enabled,
  })
}

export function useMemberCatalogOfferQuery(productId: string, enabled: boolean) {
  return useQuery({
    queryKey: memberAccessKeys.catalog(productId),
    queryFn: () => fetchMemberCatalogOffer(productId),
    enabled,
  })
}

export function useMemberProductQuery(productId: string, enabled: boolean) {
  return useQuery({
    queryKey: memberAccessKeys.product(productId),
    queryFn: () => fetchMemberProduct(productId),
    enabled,
  })
}

export function useMemberLessonQuery(productId: string, lessonId: string, enabled: boolean) {
  return useQuery({
    queryKey: memberAccessKeys.lesson(productId, lessonId),
    queryFn: () => fetchMemberLesson(productId, lessonId),
    enabled,
  })
}

export function useRequestMemberLogin() {
  return useMutation({ mutationFn: ({ producerId, email }: { producerId: string; email: string }) => requestMemberLogin(producerId, email) })
}

export function useVerifyMemberCode() {
  return useMutation({
    mutationFn: ({ producerId, email, code }: { producerId: string; email: string; code: string }) =>
      verifyMemberCode(producerId, email, code),
  })
}

export function useConsumeMemberMagic() {
  return useMutation({
    mutationFn: ({ producerId, token }: { producerId: string; token: string }) => consumeMemberMagic(producerId, token),
  })
}

export function useLoginMemberPassword() {
  return useMutation({
    mutationFn: ({ producerId, email, password }: { producerId: string; email: string; password: string }) =>
      loginMemberPassword(producerId, email, password),
  })
}

export function usePatchMemberMe() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (name: string) => patchMemberMe(name),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: memberAccessKeys.me })
    },
  })
}

export function useChangeMemberPassword() {
  return useMutation({
    mutationFn: ({ currentPassword, password }: { currentPassword: string; password: string }) =>
      changeMemberPassword(currentPassword, password),
  })
}

export function useUploadMemberAvatar() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (file: File) => uploadMemberAvatar(file),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: memberAccessKeys.me })
    },
  })
}

export function useRecordMemberProgress(productId: string, lessonId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ positionSeconds, durationSeconds }: { positionSeconds: number; durationSeconds: number }) =>
      recordMemberProgress(productId, lessonId, positionSeconds, durationSeconds),
    onSuccess: (_data, { positionSeconds }) => {
      queryClient.setQueryData<MemberLesson>(memberAccessKeys.lesson(productId, lessonId), (current) => {
        if (!current) return current
        return {
          ...current,
          lesson: { ...current.lesson, positionSeconds },
          course: {
            ...current.course,
            modules: current.course.modules.map((module) => ({
              ...module,
              lessons: module.lessons.map((item) =>
                item.id === lessonId ? { ...item, positionSeconds } : item,
              ),
            })),
          },
        }
      })
    },
  })
}

export function useCompleteMemberLesson(productId: string, lessonId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => completeMemberLesson(productId, lessonId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: memberAccessKeys.lesson(productId, lessonId) })
      void queryClient.invalidateQueries({ queryKey: memberAccessKeys.product(productId) })
      void queryClient.invalidateQueries({ queryKey: memberAccessKeys.library })
    },
  })
}
